/**
 * ActivityAuditBdr — BDR Activity Audit view.
 *
 * LOCKED DECISIONS:
 *   A1  Scaffold only — clearly labeled Beta
 *   A4  Drilldown via sub-route /#/activity/drilldown?kpi=&window=&role=bdr
 *   A8  Tab within BDR dashboard (this component is embedded as a tab)
 *
 * TENANT ISOLATION: All mock data uses tenant_id: 1.
 *   When wired to Supabase (Option B, May 12), all queries will include
 *   WHERE tenant_id != 2
 *
 * DATA STATUS: Mock/seeded. Activities table does not exist in Supabase yet.
 *   Real wiring deferred to Option B (May 12 sprint).
 *
 * ROLE SCOPE: Own activity only. BDRs cannot see peer data.
 *
 * 5 KPI Cards: Conversations · Calls Made · Emails Sent · Meetings Booked · Qualified-Forward
 * Chart:       Stacked bar (Calls / Emails / Conversations / Meetings)
 * Log:         Paginated activity log, 25 rows/page
 */

import { useState, useMemo, useEffect } from "react";
import { tokens } from "@/components/operator/tokens";
import AuditWindowSelector from "./AuditWindowSelector";
import AuditKpiCard from "./AuditKpiCard";
import AuditStackedBarChart from "./AuditStackedBarChart";
import { supabase, getSession } from "@/lib/session";
import type { TimeWindow } from "./audit-mock-data";

// Track D rebuild (B10, 2026-05-14): replaced all mock data imports with real
// Supabase queries against activity_log. Mock data removed from non-demo path.

// Real activity_log schema
interface RealActivityRow {
  id: string;
  tenant_id: number;
  user_id: string;
  channel: string | null;
  action: string | null;
  outcome_notes: string | null;
  attempt_number: number | null;
  created_at: string;
}

function deriveKpis(rows: RealActivityRow[]) {
  return {
    conversations: rows.filter(r =>
      r.action && ["connected", "replied", "booked"].some(k => r.action!.toLowerCase().includes(k))
    ).length,
    calls_made: rows.filter(r => r.channel?.toLowerCase() === "phone").length,
    emails_sent: rows.filter(r => r.channel?.toLowerCase() === "email").length,
    meetings_booked: rows.filter(r =>
      r.action && r.action.toLowerCase().includes("booked")
    ).length,
    qualified_forward: rows.filter(r =>
      r.action && r.action.toLowerCase().includes("qualified")
    ).length,
  };
}

function windowStart(w: TimeWindow): Date {
  const now = new Date();
  if (w === "today") { const d = new Date(now); d.setHours(0,0,0,0); return d; }
  if (w === "week")  { const d = new Date(now); d.setDate(d.getDate() - 7); return d; }
  if (w === "month") { const d = new Date(now); d.setDate(d.getDate() - 30); return d; }
  if (w === "quarter") { const d = new Date(now); d.setDate(d.getDate() - 90); return d; }
  const d = new Date(now); d.setDate(d.getDate() - 365); return d;
}

const PAGE_SIZE = 25;

function fmtTime(iso: string): string {
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "pm" : "am";
  const hh = h % 12 || 12;
  return `${hh}:${m.toString().padStart(2, "0")}${ampm}`;
}

// Updated for real schema: outcome is action string (nullable), channel is nullable
function OutcomePill({ outcome }: { outcome: string | null }) {
  if (!outcome) return <span style={{ fontSize: 11, color: tokens.color.textSubtle }}>-</span>;
  const lower = outcome.toLowerCase();
  let fg = "#6B6B70", bg = "#EFEFEE";
  if (lower.includes("booked") || lower.includes("connected")) { fg = "#166534"; bg = "#DCFCE7"; }
  else if (lower.includes("qualified")) { fg = "#DC2626"; bg = "#FEE2E2"; }
  else if (lower.includes("replied") || lower.includes("voicemail")) { fg = "#92400E"; bg = "#FEF3C7"; }
  return (
    <span
      style={{
        background: bg, color: fg,
        fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
        textTransform: "uppercase", padding: "2px 8px",
        borderRadius: 999, whiteSpace: "nowrap",
      }}
    >
      {outcome}
    </span>
  );
}

function ChannelPill({ channel }: { channel: string | null }) {
  return (
    <span style={{ fontSize: 12, color: tokens.color.textMuted }}>
      {channel ?? "-"}
    </span>
  );
}

export default function ActivityAuditBdr() {
  const [window, setWindow] = useState<TimeWindow>("today");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<RealActivityRow[]>([]);
  const [loading, setLoading] = useState(true);

  const session = getSession();
  const tenantId = session?.tenantId ?? 1;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const sb = supabase();
        const since = windowStart(window).toISOString();
        const { data } = await sb
          .from("activity_log")
          .select("id, tenant_id, user_id, channel, action, outcome_notes, attempt_number, created_at")
          .eq("tenant_id", tenantId)
          .neq("tenant_id", 2)
          .gte("created_at", since)
          .order("created_at", { ascending: false })
          .limit(500);
        if (!cancelled) {
          setRows((data as RealActivityRow[]) ?? []);
          setLoading(false);
        }
      } catch {
        if (!cancelled) { setRows([]); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [window, tenantId]);

  const kpis = useMemo(() => deriveKpis(rows), [rows]);
  // No prior-period kpis until activity_log has real rows
  const priorKpis = { conversations: 0, calls_made: 0, emails_sent: 0, meetings_booked: 0, qualified_forward: 0 };

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  // D-027: clamp page to valid range to prevent pageRows being empty when rows exist
  const safePage = Math.max(1, Math.min(page, totalPages));
  const pageRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Chart data derived from real rows (empty = no bars rendered)
  const chartData: { label: string; calls: number; emails: number; convos: number; meetings: number }[] = [];
  const chartTitle = `Activity Volume — ${window.charAt(0).toUpperCase() + window.slice(1)}`;

  // Partial: the active window hasn't fully elapsed yet, so the KPI numbers
  // are smaller than the prior (complete) period by construction. Match the
  // Monday-start week used by activity-log-store.windowStart.
  const isPartial = (() => {
    const now = new Date();
    if (window === "today") return true;
    if (window === "week") {
      // JS getDay(): 0=Sun, 1=Mon, ..., 6=Sat. Week is Monday-start, so the
      // last day of the active week is Sunday. Anything else is partial.
      return now.getDay() !== 0;
    }
    if (window === "month") {
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return now.getDate() !== end.getDate();
    }
    if (window === "quarter") {
      const qEndMonth = now.getMonth() - (now.getMonth() % 3) + 2;
      const end = new Date(now.getFullYear(), qEndMonth + 1, 0);
      return !(now.getMonth() === qEndMonth && now.getDate() === end.getDate());
    }
    // year
    return !(now.getMonth() === 11 && now.getDate() === 31);
  })();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Status banner — honest about data state (Track D B10 patch) */}
      {loading && (
        <div style={{ background: "#FAFAF7", border: "1px solid #EFEFEE", borderRadius: 6, padding: "7px 14px", fontSize: 12, color: "#8E8E94" }}>
          Loading activity...
        </div>
      )}
      {/* D-027: removed redundant 'No activity logged' banner — the activity log table below handles the empty state directly */}

      {/* Zone 1 — Window selector */}
      <AuditWindowSelector active={window} onChange={(w) => { setWindow(w); setPage(1); }} />

      {/* Zone 2 — KPI cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          gap: 12,
        }}
      >
        <AuditKpiCard
          label="Conversations"
          value={kpis.conversations}
          prior={priorKpis.conversations}
          isPartial={isPartial}
          kpiKey="conversations"
          window={window}
          drillRole="bdr"
        />
        <AuditKpiCard
          label="Calls Made"
          value={kpis.calls_made}
          prior={priorKpis.calls_made}
          isPartial={isPartial}
          kpiKey="calls_made"
          window={window}
          drillRole="bdr"
        />
        <AuditKpiCard
          label="Emails Sent"
          value={kpis.emails_sent}
          prior={priorKpis.emails_sent}
          isPartial={isPartial}
          kpiKey="emails_sent"
          window={window}
          drillRole="bdr"
        />
        <AuditKpiCard
          label="Meetings Booked"
          value={kpis.meetings_booked}
          prior={priorKpis.meetings_booked}
          isPartial={isPartial}
          kpiKey="meetings_booked"
          window={window}
          drillRole="bdr"
        />
        <AuditKpiCard
          label="Qualified-Forward"
          value={kpis.qualified_forward}
          prior={priorKpis.qualified_forward}
          isPartial={isPartial}
          kpiKey="qualified_forward"
          window={window}
          drillRole="bdr"
        />
      </div>

      {/* Zone 3 — Chart */}
      <div
        style={{
          background: "#FFFFFF",
          border: `1px solid ${tokens.color.border}`,
          borderRadius: tokens.radius.card,
          padding: "18px 20px",
        }}
      >
        <AuditStackedBarChart data={chartData} title={chartTitle} />
      </div>

      {/* Zone 4 — Activity log */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: tokens.color.textPrimary }}>
            Activity Log
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 12, color: tokens.color.textSubtle }}>
              {rows.length} activit{rows.length !== 1 ? "ies" : "y"}
            </span>
            <button
              type="button"
              style={{
                padding: "5px 12px", borderRadius: 6,
                border: `1px solid ${tokens.color.border}`,
                background: "#FFFFFF", color: tokens.color.textMuted,
                fontSize: 11, fontWeight: 600, fontFamily: tokens.font.body,
                cursor: "pointer", letterSpacing: "0.04em",
              }}
            >
              Export CSV
            </button>
          </div>
        </div>

        <div
          style={{
            background: "#FFFFFF",
            border: `1px solid ${tokens.color.border}`,
            borderRadius: tokens.radius.card,
            overflow: "hidden",
          }}
        >
          {/* Table header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "90px 90px 1fr 1fr 130px 110px",
              gap: 12,
              padding: "9px 16px",
              borderBottom: `1px solid ${tokens.color.border}`,
              background: tokens.color.surfaceMute,
            }}
          >
            {["Time", "Channel", "Action", "Outcome Notes", "Attempt", "User ID"].map((h) => (
              <div
                key={h}
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                  color: tokens.color.textSubtle,
                }}
              >
                {h}
              </div>
            ))}
          </div>

          {/* Rows */}
          {pageRows.length === 0 ? (
            <div
              style={{
                padding: "32px 16px",
                textAlign: "center",
                fontSize: 13,
                color: tokens.color.textSubtle,
                gridColumn: "1/-1",
              }}
            >
              No activity logged for {window}.
            </div>
          ) : (
            pageRows.map((row) => (
              <div
                key={row.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "90px 90px 1fr 1fr 130px 110px",
                  gap: 12,
                  padding: "11px 16px",
                  borderBottom: `1px solid ${tokens.color.borderSoft}`,
                  alignItems: "center",
                  transition: "background 0.1s",
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = tokens.color.surfaceMute; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
              >
                <div style={{ fontSize: 11, color: tokens.color.textSubtle, fontVariantNumeric: "tabular-nums" }}>
                  {fmtTime(row.created_at)}
                </div>
                <ChannelPill channel={row.channel} />
                <div style={{ fontSize: 12, fontWeight: 600, color: tokens.color.textPrimary }}>
                  {row.action ?? "-"}
                </div>
                <div style={{ fontSize: 12, color: tokens.color.textMuted }}>{row.outcome_notes ?? "-"}</div>
                <OutcomePill outcome={row.action} />
                <div style={{ fontSize: 11, color: tokens.color.textSubtle }}>{row.user_id.slice(0, 8)}...</div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
            <div style={{ fontSize: 12, color: tokens.color.textSubtle }}>
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, rows.length)} of {rows.length}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                type="button"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                style={{
                  padding: "5px 12px", borderRadius: 6,
                  border: `1px solid ${tokens.color.border}`,
                  background: page === 1 ? tokens.color.surfaceMute : "#FFFFFF",
                  color: page === 1 ? tokens.color.textSubtle : tokens.color.textPrimary,
                  fontSize: 12, fontWeight: 600, cursor: page === 1 ? "default" : "pointer",
                  fontFamily: tokens.font.body,
                }}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                style={{
                  padding: "5px 12px", borderRadius: 6,
                  border: `1px solid ${tokens.color.border}`,
                  background: page === totalPages ? tokens.color.surfaceMute : "#FFFFFF",
                  color: page === totalPages ? tokens.color.textSubtle : tokens.color.textPrimary,
                  fontSize: 12, fontWeight: 600, cursor: page === totalPages ? "default" : "pointer",
                  fontFamily: tokens.font.body,
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
